// Evidence → Builder import handoff.
//
// The Evidence page lets a visitor open a published candidate's EXACT frozen strategy
// (the block graph sealed in its public artifact) on the Builder canvas. Signed-out
// visitors are funneled through sign-up first: the payload waits in localStorage and is
// applied by StrategyContext the moment an authenticated session exists.
//
// Honesty invariants: the payload carries the candidate's verdict vocabulary verbatim
// ('Optimizer-qualified' from the Showcase, 'OOB-confirmed' from Strict Evidence) plus
// artifact provenance; the Builder banner renders it as history, never as a promise.
import type { Block } from '../types';

export const PENDING_EVIDENCE_IMPORT_KEY = 'tradeatory.strategy.pendingImport';

export interface EvidenceImportPayload {
  v: 1;
  /** which evidence surface produced it */
  source: 'evidence-showcase' | 'evidence-strict';
  /** run id (showcase) or candidate id (strict) */
  id: string;
  name: string;
  /** verbatim verdict vocabulary — 'Optimizer-qualified' | 'OOB-confirmed' */
  verdict: string;
  blocks: Block[];
  provenance: {
    artifactHref: string;
    paramHash?: string | null;
    market?: string;
    symbol?: string;
    timeframe?: string;
    plan?: string | null;
    round?: string | null;
  };
  stashedAt: number;
}

/** Fired after a stash so an ALREADY-signed-in session applies the import immediately
 *  (auth-state events won't refire mid-session; StrategyContext listens for this). */
export const EVIDENCE_IMPORT_EVENT = 'tradeatory:evidence-import';

export function stashEvidenceImport(p: Omit<EvidenceImportPayload, 'v' | 'stashedAt'>): void {
  const payload: EvidenceImportPayload = { v: 1, stashedAt: Date.now(), ...p };
  // Sanitize at WRITE too: an invalid href is never stored as trusted (peek also
  // re-sanitizes, so an already-persisted bad value is still neutralized on read).
  if (payload.provenance) payload.provenance = { ...payload.provenance, artifactHref: sanitizeArtifactHref(payload.provenance.artifactHref) };
  window.localStorage.setItem(PENDING_EVIDENCE_IMPORT_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent(EVIDENCE_IMPORT_EVENT));
}

// Defense-in-depth: hrefs that came through localStorage bind to <a href> in the
// Builder banner / Backtest panel. Published artifacts live ONLY under /samples/ and
// /research/, as plain static paths — so the sanitizer is an allow-list over the
// CANONICAL value, applied at BOTH write (stash) and read (peek) time.
//
// CONTRACT — the accepted value is fully canonical and decode-stable:
//   1. Canonicalize ONCE with decodeURIComponent. A malformed escape ('%', '%2',
//      '%GG') makes decodeURIComponent throw → we fail closed to ''. This is the
//      ONLY decode; step 2 then guarantees no consumer needs to decode again.
//   2. Require decoded === input. Any value carrying percent-encoding (single or
//      double: '%252e%252e', '%256a...') changes under decode and is rejected — so
//      the returned value can never "become dangerous" if a downstream component
//      decodes it, because it contains no escapes to decode.
//   3. Reject any char outside printable ASCII (kills NUL/\t/\n/DEL and every Unicode
//      look-alike/RTL/zero-width trick).
//   4. Allow-list the canonical form: '/samples/…' or '/research/…' over the charset
//      [A-Za-z0-9._/-] only — which excludes ':' '%' '\' '<' '>' '"' '\'' '?' '#' and
//      space, so no scheme (javascript:/data:/vbscript:), authority, query, fragment,
//      or attribute-break can survive; plus no '..' segment and no '//' run (traversal
//      / protocol-relative). Rejection returns '' — an INERT empty href, never the
//      original attacker-controlled string echoed into any other attribute.
export const sanitizeArtifactHref = (h: unknown): string => {
  if (typeof h !== 'string' || h.length > 512) return '';
  let decoded: string;
  try { decoded = decodeURIComponent(h); } catch { return ''; }   // malformed escape → fail closed
  if (decoded !== h) return '';                                    // must already be canonical (decode-stable)
  if (/[^\x20-\x7E]/.test(h)) return '';                           // printable-ASCII only
  if (!/^\/(samples|research)\/[A-Za-z0-9._/-]+$/.test(h)) return '';
  if (h.includes('..') || h.includes('//')) return '';
  return h;
};
const safeHref = sanitizeArtifactHref;

export function peekEvidenceImport(): EvidenceImportPayload | null {
  try {
    const raw = window.localStorage.getItem(PENDING_EVIDENCE_IMPORT_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as EvidenceImportPayload;
    if (p?.v !== 1 || !Array.isArray(p.blocks) || p.blocks.length === 0) return null;
    if (p.provenance) p.provenance.artifactHref = safeHref(p.provenance.artifactHref);
    return p;
  } catch {
    return null;
  }
}

export function clearEvidenceImport(): void {
  try { window.localStorage.removeItem(PENDING_EVIDENCE_IMPORT_KEY); } catch { /* noop */ }
}

// ── Backtest & Validate reproduction preset ──────────────────────────────────
// Stashed alongside the strategy import when the visitor clicks "Load in Backtest &
// Validate": the Backtest panel presets the timeframe + date window of the published
// run and shows the expected numbers with their honesty basis.
export const BACKTEST_PRESET_KEY = 'tradeatory.backtest.pendingPreset';

export interface BacktestPreset {
  v: 1;
  templateId: string;
  name: string;
  verdict: string;
  symbol: string;
  market: string;
  timeframe: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  expected: { trades: number | null; netUsd: number | null; basis: string };
  artifactHref: string;
  stashedAt: number;
}

export function stashBacktestPreset(p: Omit<BacktestPreset, 'v' | 'stashedAt'>): void {
  // Sanitize at WRITE too (takeBacktestPreset also re-sanitizes on read).
  const clean = { ...p, artifactHref: sanitizeArtifactHref(p.artifactHref) };
  window.localStorage.setItem(BACKTEST_PRESET_KEY, JSON.stringify({ v: 1, stashedAt: Date.now(), ...clean }));
}
// PEEK, don't consume: an early mount (auth gate remount, tab pre-render) must not
// swallow the preset before the panel that will actually apply it exists. The store is
// cleared only by clearBacktestPreset() after successful application (or on expiry).
const PRESET_TTL_MS = 15 * 60 * 1000;
export function takeBacktestPreset(): BacktestPreset | null {
  try {
    const raw = window.localStorage.getItem(BACKTEST_PRESET_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as BacktestPreset & { stashedAt?: number };
    if (p?.v !== 1) return null;
    if (p.stashedAt && Date.now() - p.stashedAt > PRESET_TTL_MS) {
      window.localStorage.removeItem(BACKTEST_PRESET_KEY);
      return null;
    }
    p.artifactHref = safeHref(p.artifactHref);
    return p;
  } catch { return null; }
}
export function clearBacktestPreset(): void {
  try { window.localStorage.removeItem(BACKTEST_PRESET_KEY); } catch { /* ignore */ }
}
